import React from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { Truck, MapPin, RotateCcw, CreditCard, ShieldCheck, Store, Package } from 'lucide-react';

import css from './ShippingInfoSection.module.css';

/**
 * ShippingInfoSection displays shipping, returns, and payment information
 * in an eBay-style format below the action buttons.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS class
 * @param {string} [props.rootClassName] - Override root CSS class
 * @param {boolean} [props.shippingEnabled=false] - Whether shipping is available
 * @param {boolean} [props.pickupEnabled=false] - Whether pickup is available
 * @param {Object} [props.shippingPrice] - Shipping price Money object
 * @param {string} [props.itemLocation] - Item location string
 * @param {string} [props.returnsPolicy] - Returns policy description
 * @param {number} [props.returnsDays=14] - Number of days for returns
 * @param {Array<string>} [props.paymentMethods] - Accepted payment methods
 * @param {string} props.marketplaceCurrency - Marketplace currency code
 * @returns {JSX.Element} Shipping info section component
 */
const ShippingInfoSection = props => {
  const intl = useIntl();
  const {
    className,
    rootClassName,
    shippingEnabled = false,
    pickupEnabled = false,
    shippingPrice,
    freeShipping = false, // Explicit free shipping flag from listing
    itemLocation,
    returnsPolicy,
    returnsDays, // No default - only show if explicitly set
    paymentMethods = ['card', 'klarna'],
    marketplaceCurrency,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  // Format shipping price - only show "Free" if explicitly set, otherwise show price or "See checkout"
  const getShippingText = () => {
    if (!shippingEnabled) return null;
    if (freeShipping || shippingPrice?.amount === 0) {
      return intl.formatMessage({ id: 'ShippingInfoSection.freeShipping' });
    }
    if (shippingPrice && shippingPrice.amount > 0) {
      return intl.formatMessage(
        { id: 'ShippingInfoSection.shippingCost' },
        { cost: formatMoney(intl, shippingPrice) }
      );
    }
    // If no shipping price set, show "calculated at checkout"
    return intl.formatMessage({ id: 'ShippingInfoSection.shippingAtCheckout' });
  };

  const shippingText = getShippingText();

  // Payment method display names
  const paymentMethodLabels = {
    card: 'Credit/Debit Card',
    klarna: 'Klarna',
    paypal: 'PayPal',
    apple_pay: 'Apple Pay',
    google_pay: 'Google Pay',
  };

  return (
    <div className={classes}>
      {/* Delivery Section */}
      <div className={css.section}>
        <h4 className={css.sectionTitle}>
          <FormattedMessage id="ShippingInfoSection.deliveryTitle" />
        </h4>

        {/* Shipping option */}
        {shippingEnabled && (
          <div className={css.infoRow}>
            <div className={css.iconWrapper}>
              <Truck className={css.icon} />
            </div>
            <div className={css.infoContent}>
              <span
                className={classNames(css.infoLabel, {
                  [css.freeShipping]: !shippingPrice || shippingPrice.amount === 0,
                })}
              >
                {shippingText}
              </span>
              <span className={css.infoDetail}>
                <FormattedMessage id="ShippingInfoSection.shippingEstimate" />
              </span>
            </div>
          </div>
        )}

        {/* Pickup option */}
        {pickupEnabled && (
          <div className={css.infoRow}>
            <div className={css.iconWrapper}>
              <Store className={css.icon} />
            </div>
            <div className={css.infoContent}>
              <span className={css.infoLabel}>
                <FormattedMessage id="ShippingInfoSection.pickupAvailable" />
              </span>
              <span className={css.infoDetail}>
                <FormattedMessage id="ShippingInfoSection.pickupDetail" />
              </span>
            </div>
          </div>
        )}

        {/* Item location */}
        {itemLocation && (
          <div className={css.infoRow}>
            <div className={css.iconWrapper}>
              <MapPin className={css.icon} />
            </div>
            <div className={css.infoContent}>
              <span className={css.infoLabel}>
                <FormattedMessage id="ShippingInfoSection.itemLocation" />
              </span>
              <span className={css.infoDetail}>{itemLocation}</span>
            </div>
          </div>
        )}
      </div>

      {/* Returns Section - only show if return policy is set */}
      {returnsDays && (
        <div className={css.section}>
          <h4 className={css.sectionTitle}>
            <FormattedMessage id="ShippingInfoSection.returnsTitle" />
          </h4>
          <div className={css.infoRow}>
            <div className={css.iconWrapper}>
              <RotateCcw className={css.icon} />
            </div>
            <div className={css.infoContent}>
              <span className={css.infoLabel}>
                <FormattedMessage
                  id="ShippingInfoSection.returnsDays"
                  values={{ days: returnsDays }}
                />
              </span>
              <span className={css.infoDetail}>
                {returnsPolicy || <FormattedMessage id="ShippingInfoSection.returnsDefault" />}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Payment Section */}
      <div className={css.section}>
        <h4 className={css.sectionTitle}>
          <FormattedMessage id="ShippingInfoSection.paymentTitle" />
        </h4>
        <div className={css.infoRow}>
          <div className={css.iconWrapper}>
            <CreditCard className={css.icon} />
          </div>
          <div className={css.infoContent}>
            <span className={css.infoLabel}>
              <FormattedMessage id="ShippingInfoSection.acceptedPayments" />
            </span>
            <div className={css.paymentMethods}>
              {paymentMethods.map(method => (
                <span key={method} className={css.paymentBadge}>
                  {paymentMethodLabels[method] || method}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Buyer Protection */}
      <div className={css.section}>
        <div className={css.buyerProtectionRow}>
          <div className={css.iconWrapper}>
            <ShieldCheck className={css.protectionIcon} />
          </div>
          <div className={css.infoContent}>
            <span className={css.protectionLabel}>
              <FormattedMessage id="ShippingInfoSection.buyerProtection" />
            </span>
            <span className={css.infoDetail}>
              <FormattedMessage id="ShippingInfoSection.buyerProtectionDetail" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingInfoSection;
